import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { onSnapshot, doc } from 'firebase/firestore';
import { db } from '../services/firebase';
import subscriptionService from '../services/subscriptionService';
import {
  TeacherSubscription,
  SubscriptionPlan,
  SubscriptionPlanId,
  BillingCycle,
  PLAN_ENTITLEMENTS,
  normalizeTeacherSubscription,
} from '../types/subscription';
import { useAuth } from '../hooks/useAuth';

interface SubscriptionContextType {
  subscription: TeacherSubscription | null;
  plan: SubscriptionPlan;
  loading: boolean;
  isFeatureAllowed: (feature: keyof SubscriptionPlan['limits']) => boolean;
  checkStudentLimit: (currentCount: number) => LimitCheck;
  checkClassLimit: (currentCount: number) => LimitCheck;
  checkSessionLimit: (currentCount: number) => LimitCheck;
  changePlan: (planId: SubscriptionPlanId, cycle: BillingCycle) => Promise<void>;
  cancelSubscription: () => Promise<void>;
  daysUntilExpiry: number;
  isReadOnly: boolean;
}

interface LimitCheck {
  allowed: boolean;
  limit: number;
  upgradeRequired: SubscriptionPlanId | null;
}

const TRIAL_PLAN = PLAN_ENTITLEMENTS.trial;

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<TeacherSubscription | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id || user.role !== 'teacher') {
      setLoading(false);
      return;
    }

    // Bootstrap the server-owned 14-day trial when no subscription exists.
    subscriptionService.getOrCreateSubscription(user.id).catch(console.error);

    // Real-time listener
    const unsub = onSnapshot(
      doc(db, 'subscriptions', user.id),
      (snap) => {
        if (snap.exists()) {
          setSubscription(normalizeTeacherSubscription(snap.data() as any));
        }
        setLoading(false);
      },
      (error) => {
        console.error('Subscription listener error:', error);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [user?.id, user?.role]);

  const plan = subscription
    ? PLAN_ENTITLEMENTS[subscription.planId]
    : TRIAL_PLAN;
  const periodHasEnded = subscription
    ? subscription.currentPeriodEnd.toMillis() <= Date.now()
    : false;
  const isReadOnly =
    !subscription ||
    subscription.status === 'read_only' ||
    subscription.status === 'expired' ||
    (subscription.planId === 'trial' && periodHasEnded);

  const isFeatureAllowed = useCallback(
    (feature: keyof SubscriptionPlan['limits']): boolean => {
      return Boolean(plan.limits[feature]);
    },
    [plan]
  );

  const checkStudentLimit = useCallback(
    (currentCount: number): LimitCheck => {
      if (!subscription || isReadOnly) return { allowed: false, limit: plan.limits.maxStudents, upgradeRequired: 'solo' };
      return subscriptionService.checkLimit(subscription, 'students', currentCount);
    },
    [isReadOnly, plan.limits.maxStudents, subscription]
  );

  const checkClassLimit = useCallback(
    (currentCount: number): LimitCheck => {
      if (!subscription || isReadOnly) return { allowed: false, limit: plan.limits.maxClasses, upgradeRequired: 'solo' };
      return subscriptionService.checkLimit(subscription, 'classes', currentCount);
    },
    [isReadOnly, plan.limits.maxClasses, subscription]
  );

  const checkSessionLimit = useCallback(
    (currentCount: number): LimitCheck => {
      if (!subscription || isReadOnly) return { allowed: false, limit: plan.limits.maxSessionsPerMonth, upgradeRequired: 'solo' };
      return subscriptionService.checkLimit(subscription, 'sessions', currentCount);
    },
    [isReadOnly, plan.limits.maxSessionsPerMonth, subscription]
  );

  const changePlan = useCallback(
    async (planId: SubscriptionPlanId, cycle: BillingCycle) => {
      if (!user?.id) return;
      await subscriptionService.changePlan(user.id, planId, cycle);
    },
    [user?.id]
  );

  const cancelSubscription = useCallback(async () => {
    if (!user?.id) return;
    await subscriptionService.cancelSubscription(user.id);
  }, [user?.id]);

  const daysUntilExpiry = subscription
    ? Math.max(0, Math.ceil((subscription.currentPeriodEnd.toDate().getTime() - Date.now()) / 86400000))
    : 0;

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        plan,
        loading,
        isFeatureAllowed,
        checkStudentLimit,
        checkClassLimit,
        checkSessionLimit,
        changePlan,
        cancelSubscription,
        daysUntilExpiry,
        isReadOnly,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider');
  return ctx;
};
